/**
 * FieldErrorBoundary -- catches rendering errors in individual dynamic fields
 * so that one broken field does not crash the entire connector wizard form.
 *
 * Renders a small inline fallback showing the field name and a "Retry" button
 * that resets the error state to attempt re-rendering the child.
 */
import React from 'react';
import { AlertCircle } from 'lucide-react';

interface FieldErrorBoundaryProps {
  fieldName: string;
  children: React.ReactNode;
}

interface FieldErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class FieldErrorBoundary extends React.Component<
  FieldErrorBoundaryProps,
  FieldErrorBoundaryState
> {
  constructor(props: FieldErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): FieldErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(
      `FieldErrorBoundary caught error in field "${this.props.fieldName}":`,
      error,
      errorInfo
    );
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-destructive text-sm"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>
            Failed to render field <strong>{this.props.fieldName}</strong>
          </span>
          <button
            type="button"
            onClick={this.handleRetry}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
